"use client"

import { useState, useEffect } from "react"
import { addStudent, editStudent, fetchBatches } from "../comman/Student.js"
import { fetchAcademyData } from "../comman/Academy.js"
import axios from "axios"

const BACKEND = process.env.REACT_APP_BACKEND_URL

const AddStudentModal = ({ isOpen, onClose, studentToEdit, onAddStudent, onUpdateStudent }) => {
  const [batches, setBatches] = useState([])
  const [selectedAcademy, setSelectedAcademy] = useState("")
  const [extras, setExtras] = useState([])
  const [basePrice, setBasePrice] = useState("")
  const [advance, setAdvance] = useState("")
  const [discount, setDiscount] = useState("")
  const [total, setTotal] = useState(0)
  const [paymentLink, setPaymentLink] = useState("")
  const [dueDate, setDueDate] = useState("")
  const [paymentStatus, setPaymentStatus] = useState("")
  const [academies, setAcademies] = useState([])
  const [loading, setLoading] = useState(false)

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    role: "student",
    academyName: "",
    parentsContactNumber: "",
    gender: "",
    dob: "",
    batchId: "",
    studentacademyId: "",
  })

  useEffect(() => {
    fetchAcademy()
  }, [])

  useEffect(() => {
    if (studentToEdit) {
      setFormData({
        name: studentToEdit.profile?.name || "",
        phone: studentToEdit.profile?.phone || "",
        role: studentToEdit.role || "student",
        academyName: studentToEdit.academyName || "",
        parentsContactNumber: studentToEdit.profile?.parentsContactNumber || "",
        gender: studentToEdit.profile?.gender || "",
        dob: studentToEdit.profile?.dob || "",
        batchId: studentToEdit.batchId || "",
        studentacademyId: studentToEdit.studentacademyId || "",
      })
      setBasePrice(studentToEdit.fees?.basePrice || "")
      setAdvance(studentToEdit.fees?.advance || "")
      setDiscount(studentToEdit.fees?.discount || "")
      setDueDate(studentToEdit.fees?.dueDate || "")
      setPaymentStatus(studentToEdit.fees?.paymentStatus || "")
      setExtras(studentToEdit.fees?.extras || [])
    }
  }, [studentToEdit])

  useEffect(() => {
    calculateTotal()
  }, [basePrice, advance, discount, extras])

  const fetchAcademy = async () => {
    setLoading(true)
    try {
      const data = await fetchAcademyData()
      setAcademies(data)
    } catch (err) {
      console.error("Error fetching academies:", err)
    } finally {
      setLoading(false)
    }
  }

  const fetchBatch = async (academyId) => {
    try {
      const response = await fetchBatches(academyId)
      setBatches(response)
    } catch (error) {
      console.error(error.message)
    }
  }

  const generateID = () => {
    const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ"
    const randomLetters =
      letters.charAt(Math.floor(Math.random() * letters.length)) +
      letters.charAt(Math.floor(Math.random() * letters.length))
    const randomDigits = Math.floor(1000 + Math.random() * 9000)
    return `${randomLetters}${randomDigits}`
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prevData) => {
      const updatedData = { ...prevData, [name]: value }

      if (name === "academyName") {
        setSelectedAcademy(value)
        const selectedAcademyObj = academies.find((a) => a.name === value)
        if (selectedAcademyObj) {
          fetchBatch(selectedAcademyObj._id)
        }
      }

      if (name === "batchId" && value && !updatedData.studentacademyId) {
        updatedData.studentacademyId = generateID()
      }

      return updatedData
    })
  }

  const addExtraField = () => {
    setExtras([...extras, { name: "", price: "" }])
  }

  const updateExtraField = (index, field, value) => {
    const updatedExtras = extras.map((extra, idx) => (idx === index ? { ...extra, [field]: value } : extra))
    setExtras(updatedExtras)
  }

  const removeExtraField = (index) => {
    setExtras(extras.filter((_, idx) => idx !== index))
  }

  const calculateTotal = () => {
    const extrasTotal = extras.reduce((acc, extra) => acc + (Number.parseFloat(extra.price) || 0), 0)
    const discountAmount = Number.parseFloat(discount) || 0
    const totalAmount =
      (Number.parseFloat(basePrice) || 0) - (Number.parseFloat(advance) || 0) + extrasTotal - discountAmount
    setTotal(totalAmount)
  }

  const generatePaymentLink = async () => {
    try {
      const response = await axios.post(`${BACKEND}/create-payment-link`, {
        amount: total,
      })

      if (response.data.success) {
        setPaymentLink(response.data.paymentLink)
      } else {
        console.error("Error", "Failed to create payment link.")
      }
    } catch (error) {
      console.error("Error generating payment link:", error)
    }
  }

  const sharePaymentLink = async () => {
    if (!paymentLink) {
      alert("Please generate a payment link first.")
      return
    }
    try {
      await navigator.clipboard.writeText(paymentLink)
      alert("Payment link copied to clipboard!")
    } catch (error) {
      alert("Failed to copy the payment link.")
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      let data

      const feesData = {
        basePrice: Number.parseFloat(basePrice) || 0,
        advance: Number.parseFloat(advance) || 0,
        total: Number.parseFloat(total) || 0,
        dueDate: dueDate,
        paymentStatus: paymentStatus,
        extras: extras.map((extra) => ({
          name: extra.name,
          price: Number.parseFloat(extra.price) || 0,
        })),
      }
      if (studentToEdit) {
        data = await editStudent(studentToEdit._id, formData, feesData)
        onUpdateStudent(data.student)
      } else {
        data = await addStudent(formData, feesData)
        onAddStudent(data.student)
      }

      alert(data.message)
      onClose()
    } catch (error) {
      console.error(error)
      alert(error.message)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white p-4 border-b flex justify-between items-center">
          <h2 className="text-2xl font-semibold text-gray-800">{studentToEdit ? "Edit Student" : "Add Student"}</h2>
          <button onClick={onClose} className="text-gray-600 hover:text-gray-800">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#12143F] focus:border-[#12143F]"
                required
              />
            </div>
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#12143F] focus:border-[#12143F]"
                required
              />
            </div>
            <div>
              <label htmlFor="academyName" className="block text-sm font-medium text-gray-700 mb-1">
                Academy Name
              </label>
              <select
                id="academyName"
                name="academyName"
                value={formData.academyName}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#12143F] focus:border-[#12143F]"
                required
              >
                <option value="">Select Academy</option>
                {academies.map((academy) => (
                  <option key={academy._id} value={academy.name}>
                    {academy.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="batchId" className="block text-sm font-medium text-gray-700 mb-1">
                Batch
              </label>
              <select
                id="batchId"
                name="batchId"
                value={formData.batchId}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#12143F] focus:border-[#12143F]"
                required
              >
                <option value="">Select Batch</option>
                {batches.map((batch) => (
                  <option key={batch._id} value={batch._id}>
                    {batch.batchName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="studentacademyId" className="block text-sm font-medium text-gray-700 mb-1">
                Academy ID
              </label>
              <input
                type="text"
                id="studentacademyId"
                name="studentacademyId"
                value={formData.studentacademyId}
                readOnly
                className="w-full p-2 border border-gray-300 rounded-md bg-gray-100"
              />
            </div>
            <div>
              <label htmlFor="parentsContactNumber" className="block text-sm font-medium text-gray-700 mb-1">
                Parent's Contact Number
              </label>
              <input
                type="tel"
                id="parentsContactNumber"
                name="parentsContactNumber"
                value={formData.parentsContactNumber}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#12143F] focus:border-[#12143F]"
                required
              />
            </div>
            <div>
              <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-1">
                Gender
              </label>
              <select
                id="gender"
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#12143F] focus:border-[#12143F]"
                required
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label htmlFor="dob" className="block text-sm font-medium text-gray-700 mb-1">
                Date of Birth
              </label>
              <input
                type="date"
                id="dob"
                name="dob"
                value={formData.dob}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#12143F] focus:border-[#12143F]"
                required
              />
            </div>
          </div>

          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Fee Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label htmlFor="basePrice" className="block text-sm font-medium text-gray-700 mb-1">
                  Base Price
                </label>
                <input
                  type="number"
                  id="basePrice"
                  value={basePrice}
                  onChange={(e) => setBasePrice(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#12143F] focus:border-[#12143F]"
                />
              </div>
              <div>
                <label htmlFor="advance" className="block text-sm font-medium text-gray-700 mb-1">
                  Advance Payment
                </label>
                <input
                  type="number"
                  id="advance"
                  value={advance}
                  onChange={(e) => setAdvance(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#12143F] focus:border-[#12143F]"
                />
              </div>
              <div>
                <label htmlFor="discount" className="block text-sm font-medium text-gray-700 mb-1">
                  Discount
                </label>
                <input
                  type="number"
                  id="discount"
                  value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#12143F] focus:border-[#12143F]"
                />
              </div>
            </div>
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-gray-900">Extras</h4>
              {extras.map((extra, index) => (
                <div key={index} className="flex space-x-2">
                  <input
                    type="text"
                    placeholder="Extra Name"
                    value={extra.name}
                    onChange={(e) => updateExtraField(index, "name", e.target.value)}
                    className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-[#12143F] focus:border-[#12143F]"
                  />
                  <input
                    type="number"
                    placeholder="Price"
                    value={extra.price}
                    onChange={(e) => updateExtraField(index, "price", e.target.value)}
                    className="w-24 p-2 border border-gray-300 rounded-md focus:ring-[#12143F] focus:border-[#12143F]"
                  />
                  <button
                    type="button"
                    onClick={() => removeExtraField(index)}
                    className="px-2 py-1 bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addExtraField}
                className="text-[#12143F] hover:text-opacity-80 focus:outline-none"
              >
                + Add Extra Field
              </button>
            </div>
          </div>

          <div className="flex justify-between items-center">
            <div>
              <span className="text-lg font-medium text-gray-900">Total: </span>
              <span className="text-xl font-bold text-[#12143F]">{total}</span>
            </div>
            <div>
              <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700 mb-1">
                Due Date
              </label>
              <input
                type="date"
                id="dueDate"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="p-2 border border-gray-300 rounded-md focus:ring-[#12143F] focus:border-[#12143F]"
              />
            </div>
          </div>

          <div className="space-y-4">
            <button
              type="button"
              onClick={generatePaymentLink}
              className="w-full py-2 bg-[#12143F] text-white rounded-md hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-[#12143F] focus:ring-opacity-50"
            >
              Generate Payment Link
            </button>

            {paymentLink && (
              <div className="p-4 bg-gray-100 rounded-md">
                <p className="mb-2">
                  Payment Link:{" "}
                  <a
                    href={paymentLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    {paymentLink}
                  </a>
                </p>
                <button
                  type="button"
                  onClick={sharePaymentLink}
                  className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50"
                >
                  Share Payment Link
                </button>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#12143F] text-white rounded-md hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-[#12143F] focus:ring-opacity-50"
            >
              {studentToEdit ? "Update Student" : "Save Student"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AddStudentModal

